import posthog from "posthog-js";

// Config mirrors packages/spartan-tracking/client/posthog.js, which is the source of
// truth for how Spartan initialises PostHog. It is restated here rather than imported
// because that package publishes to a private GitLab registry and this app's Docker
// build has no token for it. Keep the two in step when either changes.
//
// Session replay is deliberately off. Replay traffic is what filled the self-hosted
// instance's Kafka volume on 2026-08-16; turn it on only alongside a retention plan.

const KEY = process.env.VUE_APP_POSTHOG_KEY;
const RAW_HOST = (
  process.env.VUE_APP_POSTHOG_PROXY_URL || "https://posthog.spartan.com"
).replace(/\/$/, "");

// A leading slash is the same-origin nginx proxy (provisioning/nginx/conf.d/proxy.conf).
// Resolved against the page origin so requests leave the browser addressed to
// account.spartan.com, which carries no blocklist rule; an absolute value still works
// for local dev, where there is no nginx in front.
let apiHost = RAW_HOST;

let ready = false;
let lastPath = null;

function pageview(path, referrer) {
  // init fires the first pageview and the router's initial afterEach fires again for
  // the same path; keying on the last path collapses that without timing assumptions.
  if (!ready || path === lastPath) return;
  lastPath = path;
  posthog.capture("$pageview", {
    $current_url: window.location.origin + path,
    $pathname: path,
    $referrer: referrer || "",
  });
}

export function initPosthog(router) {
  if (typeof window === "undefined" || !KEY || KEY === "disabled") return;

  const stage = process.env.VUE_APP_STAGE || process.env.NODE_ENV || "production";

  if (RAW_HOST.startsWith("/")) apiHost = window.location.origin + RAW_HOST;

  posthog.init(KEY, {
    api_host: apiHost,
    ip: true,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    disable_session_recording: true,
    cross_subdomain_cookie: true,
    secure_cookie: true,
    rate_limiting: { events_burst_limit: 60, events_per_second: 20 },
    loaded(ph) {
      // posthog-js >= 1.373 fetches a remote config that can reset api_host back to
      // posthog.com. loaded() runs after that, so re-assert the proxy host here.
      ph.set_config({ api_host: apiHost, ip: true });
      ph.register({ sp_app: "membership", sp_environment: stage });
      ready = true;
      pageview(window.location.pathname, document.referrer);
    },
  });

  window.posthog = posthog;

  if (typeof router?.afterEach === "function") {
    router.afterEach((to, from) => {
      if (from?.fullPath === to.fullPath) return;
      pageview(to.path, from?.name ? window.location.origin + from.fullPath : "");
    });
  }
}

export function capture(event, properties) {
  if (!ready) return;
  posthog.capture(event, properties || {});
}

// Vue.config.errorHandler swallows component errors before window.onerror sees them.
export function captureException(err, properties) {
  if (!ready) return;
  posthog.captureException(err, properties || {});
}

// These dataLayer events already have a first-class PostHog equivalent, so forwarding
// them too would double-count purchases and pageviews.
const DATALAYER_SKIP = new Set(["pageViewExperience", "Conversion", "Payment"]);

// Whole user records ride along on the auth dataLayer events. Identity belongs on the
// person profile via identifyUser, not copied onto every event.
const DATALAYER_DROP_KEYS = new Set(["userData", "pageViewInfo"]);

/**
 * Mirror a GTM dataLayer push into PostHog so the journey GTM already records
 * (login, signup, password reset, logout, Spartan+ purchase, shares, upsells)
 * is queryable alongside the events captured natively here.
 */
export function captureFromDataLayer(event, data) {
  if (!ready || !event || DATALAYER_SKIP.has(event)) return;
  const props = {};
  Object.keys(data || {}).forEach((k) => {
    if (!DATALAYER_DROP_KEYS.has(k)) props[k] = data[k];
  });
  posthog.capture(event, { ...props, source: "gtm_datalayer" });
}

export function identifyUser(user) {
  if (!ready || !user?.id) return;
  posthog.identify(String(user.id), {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    spartan_plus_member: !!user.spartan_plus_member,
    country_code: user.spartan_country_code || user.country_code,
  });
}

export function resetUser() {
  if (!ready) return;
  posthog.reset();
  lastPath = null;
}

export default { initPosthog, capture, identifyUser, resetUser };
